import { useMemo, useCallback, useRef, useState } from 'react';
import {
  emptyRow,
  isRowAtLeastOneCellComplete,
  GROUP_ENUM,
  requiredFields,
  snakeToCamel,
} from '../utils';
import ValidationService from '../services/ValidationService';

const useScheduleTableData = (
  collection,
  handleChange,
  onDelete,
  group,
  setValidationErrors,
  setEditingRows,
) => {
  const EMPTY_ROWS_COUNT = 5;
  const [localUpdates, setLocalUpdates] = useState({});

  const processedData = useMemo(() => {
    if (!collection || collection.length === 0) {
      return Array.from({ length: EMPTY_ROWS_COUNT }, (_, index) => ({
        ...emptyRow,
        _new: true,
        _emptyRowId: `empty_${index}`,
      }));
    }

    const cleanedCollection = collection.filter((row) => !(row._new === true && (!row.id || row.id === '')));

    const collectionWithLocalUpdates = cleanedCollection.map(item => {
      const itemKey = item.key || item.id || item._emptyRowId;
      if (localUpdates[itemKey]) {
        return { ...item, ...localUpdates[itemKey] };
      }
      return item;
    });

    const emptyRows = Array.from({ length: EMPTY_ROWS_COUNT }, (_, index) => {
      const emptyRowId = `empty_${collectionWithLocalUpdates.length + index}`;
      const baseEmptyRow = {
        ...emptyRow,
        _new: true,
        _emptyRowId: emptyRowId,
      };

      if (localUpdates[emptyRowId]) {
        return { ...baseEmptyRow, ...localUpdates[emptyRowId] };
      }
      return baseEmptyRow;
    });

    return [
      ...collectionWithLocalUpdates,
      ...emptyRows,
    ];
  }, [collection, localUpdates]);

  const allRowIndices = useMemo(() => {
    if (!collection || collection.length === 0) return [];

    return collection.map((_, idx) => idx).filter((idx) => {
      if (group === 'primary' && idx === 0) {
        return false;
      }
      return true;
    });
  }, [collection, group]);

  const checkDuplicateAddress = useCallback((value, record) => {
    const allData = [...processedData];
    const updatedData = allData.map(item => {
      const itemKey = item.key || item.id || item._emptyRowId;
      const currentKey = record.key || record.id || record._emptyRowId;
      return itemKey === currentKey ? { ...item, address: value } : item;
    });

    const { duplicateAddresses } = ValidationService.validateDuplicateAddresses(updatedData);
    const currentKey = record.key || record.id || record._emptyRowId;

    return duplicateAddresses.some(duplicate => {
      const duplicateKey = duplicate.id || duplicate._emptyRowId;
      return duplicateKey === currentKey;
    });
  }, [processedData]);

  const enhancedHandleChange = useCallback((value, column, record, isValid = true) => {
    let rowIndex = -1;

    rowIndex = processedData.findIndex((row) => row === record);

    if (rowIndex === -1) {
      rowIndex = processedData.findIndex((row) => {
        if (record._emptyRowId && row._emptyRowId === record._emptyRowId) {
          return true;
        }

        if (record.id && row.id && row.id === record.id && !record._new) {
          return true;
        }

        if (record.key && row.key === record.key && record.key !== emptyRow.key) {
          return true;
        }

        return false;
      });
    }

    const recordKey = record.key || record.id || record._emptyRowId;
    const isEmptyRow = record._new === true;
    const hasValue = value && value.toString().trim() !== '';

    if (!hasValue) {
      const recordWithClearedField = { ...record, [snakeToCamel(column)]: '' };
      const fields = requiredFields(GROUP_ENUM[group]);
      const isRowCompletelyEmpty = !isRowAtLeastOneCellComplete(recordWithClearedField, fields);

      if (!isEmptyRow && record.id && isRowCompletelyEmpty) {
        if (rowIndex === 0 && group === 'primary') return;
        onDelete(record);

        return;
      }

      if (isEmptyRow && isRowCompletelyEmpty) {
        setLocalUpdates(prev => {
          const newUpdates = { ...prev };
          delete newUpdates[recordKey];
          return newUpdates;
        });
      }
    }

    if (isEmptyRow && hasValue) {
      setEditingRows((prev) => new Set([...prev, rowIndex]));
    }

    const fieldKey = `${recordKey}_${column}`;

    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldKey];
      return newErrors;
    });

    const prevValue = record[snakeToCamel(column)];
    const isPrevEmpty = prevValue === undefined || prevValue === null || prevValue === '';
    const isNewEmpty = value === undefined || value === null || value === '';
    if (value === prevValue || (isPrevEmpty && isNewEmpty)) {
      return;
    }

    if (value === 0 || value === '0') {
      return;
    }

    if (column === 'address' && hasValue) {
      const isDuplicateAddress = checkDuplicateAddress(value, record);

      if (isDuplicateAddress) {
        setLocalUpdates(prev => ({
          ...prev,
          [recordKey]: {
            ...prev[recordKey],
            [snakeToCamel(column)]: value
          }
        }));
        return;
      }

      setLocalUpdates(prev => {
        const newUpdates = { ...prev };
        delete newUpdates[recordKey];
        return newUpdates;
      });

      handleChange(value, column, record);
      return;
    }

    setLocalUpdates(prev => {
      const newUpdates = { ...prev };
      delete newUpdates[recordKey];
      return newUpdates;
    });

    handleChange(value, column, record);
  }, [processedData, collection, handleChange, onDelete, group, setValidationErrors, setEditingRows, localUpdates]);

  return {
    processedData,
    allRowIndices,
    enhancedHandleChange,
  };
};

export default useScheduleTableData;
